import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";

// Pages
import HomePage from "@/pages/HomePage";
import StudentBlog from "@/pages/StudentBlog";
import BlogPost from "@/pages/BlogPost";
import PatientVideos from "@/pages/PatientVideos";
import Podcasts from "@/pages/Podcasts";
import About from "@/pages/About";
import BooksLandingPage from "@/pages/BooksLandingPage";
import { SiteContentProvider } from "@/contexts/SiteContentContext";
import BookDetailPage from "@/pages/BookDetailPage";
import BooksManager from "@/pages/BooksManager";
import MediaLibraryPage from "@/pages/MediaLibraryPage";
import InternalPractices from "@/pages/InternalPractices";
import CoursesPage from "@/pages/CoursesPage";
import CourseDetailPage from "@/pages/CourseDetailPage";
import CoursePlayerPage from "@/pages/CoursePlayerPage";
import CourseEditorPage from "@/pages/CourseEditorPage";
import TestingCenterPage from "@/pages/TestingCenterPage";
import TestingBasicPage from "@/pages/TestingBasicPage";
import TestingCompsReadyPage from "@/pages/TestingCompsReadyPage";
import TestingAdvancedPage from "@/pages/TestingAdvancedPage";
import TestingSectionPage from "@/pages/TestingSectionPage";
import TestManagementPage from "@/pages/TestManagementPage";
import TestTakingPage from "@/pages/TestTakingPage";
import StudentDashboard from "@/pages/StudentDashboard";
import StudentResources from "@/pages/StudentResources";
import WebmasterPage from "@/pages/WebmasterPage";
import AdminDashboard from "@/pages/AdminDashboard";
import GenerationPage from "@/pages/GenerationPage";
import ChapterAssemblyPage from "@/pages/ChapterAssemblyPage";
import PublicationAssemblyPage from "@/pages/PublicationAssemblyPage";
import PublicationVaultPage from "@/pages/PublicationVaultPage";
import QuizManagementPage from "@/pages/QuizManagementPage";
import AuthCallback from "@/pages/AuthCallback";
import DonationSuccess from "@/pages/DonationSuccess";
import BookingSuccess from "@/pages/BookingSuccess";
import AreaDetailPage from "@/pages/AreaDetailPage";
import AreasManagerPage from "@/pages/AreasManagerPage";
import AreaEditPage from "@/pages/AreaEditPage";
import AreaCreatePage from "@/pages/AreaCreatePage";
import SimpleCourseGenerator from "@/pages/SimpleCourseGenerator";
import SimpleBlogGenerator from "@/pages/SimpleBlogGenerator";
import SimpleLessonGenerator from "@/pages/SimpleLessonGenerator";
import ErnieAiPage from "@/pages/ErnieAiPage";
import ErnieMemoriesPage from "@/pages/ErnieMemoriesPage";
import FlashcardsCenter from "@/pages/FlashcardsCenter";
import FlashcardsCategoryPage from "@/pages/FlashcardsCategoryPage";
import MatchingCenter from "@/pages/MatchingCenter";
import MatchingCategoryPage from "@/pages/MatchingCategoryPage";
import StudyGuideCenter from "@/pages/StudyGuideCenter";
import StudyGuideCategoryPage from "@/pages/StudyGuideCategoryPage";
import FlashcardViewer from "@/pages/FlashcardViewer";
import MatchingGameViewer from "@/pages/MatchingGameViewer";
import StudyGuideViewer from "@/pages/StudyGuideViewer";
import MemoryGameCenter from "@/pages/MemoryGameCenter";
import MemoryGameCategoryPage from "@/pages/MemoryGameCategoryPage";
import FillInCenter from "@/pages/FillInCenter";
import FillInCategoryPage from "@/pages/FillInCategoryPage";
import JeopardyCenter from "@/pages/JeopardyCenter";
import JeopardyCategoryPage from "@/pages/JeopardyCategoryPage";
import MemoryGameViewer from "@/pages/MemoryGameViewer";
import FillInViewer from "@/pages/FillInViewer";
import JeopardyGameViewer from "@/pages/JeopardyGameViewer";
import PublicationCenter from "@/pages/PublicationCenter";
import MyPublications from "@/pages/MyPublications";
import PublicationReader from "@/pages/PublicationReader";
import ClassCenter from "@/pages/ClassCenter";
import ClassDetail from "@/pages/ClassDetail";
import StudyBuddy from "@/pages/StudyBuddy";
import StudyBuddySubscribe from "@/pages/StudyBuddySubscribe";
import MyCollegeCourses from "@/pages/MyCollegeCourses";
import AllCoursesCalendar from "@/pages/AllCoursesCalendar";
import CollegeCourseDashboard from "@/pages/CollegeCourseDashboard";
import SmartGoalWizard from "@/pages/SmartGoalWizard";
import SmartGoalView from "@/pages/SmartGoalView";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext(null);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Configure axios interceptor to add Authorization header from localStorage
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('session_token');
        if (token && !config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
    };
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      // Clear localStorage token if auth fails
      localStorage.removeItem('session_token');
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      // Clear localStorage token on logout
      localStorage.removeItem('session_token');
    } catch (error) {
      console.error("Logout error:", error);
      // Clear token anyway on logout attempt
      localStorage.removeItem('session_token');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#8B5A3C]">
        <div className="text-[#F4E4C1] text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser, logout }}>
      <BrowserRouter>
        <SiteContentProvider isAdmin={user?.role === 'admin'}>
        <div className="App">
          <Toaster position="top-right" />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/areas/:slug" element={<AreaDetailPage />} />
            <Route path="/student-blog" element={<StudentBlog />} />
            <Route path="/blog/:id" element={<BlogPost />} />
            <Route path="/patient-videos" element={<PatientVideos />} />
            <Route path="/podcasts" element={<Podcasts />} />
            <Route path="/about" element={<About />} />
            <Route path="/internal-practices" element={<InternalPractices />} />
            <Route path="/study-buddy" element={<StudyBuddy />} />
            <Route path="/study-buddy/subscribe" element={<StudyBuddySubscribe />} />
            <Route path="/study-buddy/my-courses" element={<MyCollegeCourses />} />
            <Route path="/study-buddy/calendar" element={<AllCoursesCalendar />} />
            <Route path="/study-buddy/course/:courseId" element={<CollegeCourseDashboard />} />
            <Route path="/study-buddy/course/:courseId/smart-goal/create" element={<SmartGoalWizard />} />
            <Route path="/study-buddy/smart-goal/:goalId" element={<SmartGoalView />} />
            <Route path="/courses" element={user ? <CoursesPage /> : <Navigate to="/" />} />
            <Route path="/courses/:id" element={user ? <CourseDetailPage /> : <Navigate to="/" />} />
            <Route path="/courses/:id/learn" element={user ? <CoursePlayerPage /> : <Navigate to="/" />} />
            <Route path="/admin/courses/new" element={user?.role === 'admin' ? <CourseEditorPage /> : <Navigate to="/" />} />
            <Route path="/admin/courses/:id/edit" element={user?.role === 'admin' ? <CourseEditorPage /> : <Navigate to="/" />} />
            <Route path="/testing-center" element={user ? <TestingCenterPage /> : <Navigate to="/" />} />
            <Route path="/testing-center/basic" element={user ? <TestingBasicPage /> : <Navigate to="/" />} />
            <Route path="/testing-center/comps-ready" element={user ? <TestingCompsReadyPage /> : <Navigate to="/" />} />
            <Route path="/testing-center/advanced" element={user ? <TestingAdvancedPage /> : <Navigate to="/" />} />
            <Route path="/testing-center/:tier/:section" element={user ? <TestingSectionPage /> : <Navigate to="/" />} />
            <Route path="/tests/:id" element={user ? <TestTakingPage /> : <Navigate to="/" />} />
            <Route path="/flashcards-center" element={user ? <FlashcardsCenter /> : <Navigate to="/" />} />
            <Route path="/flashcards-category/:tier/:categorySlug" element={user ? <FlashcardsCategoryPage /> : <Navigate to="/" />} />
            <Route path="/flashcards/:id" element={user ? <FlashcardViewer /> : <Navigate to="/" />} />
            <Route path="/matching-center" element={user ? <MatchingCenter /> : <Navigate to="/" />} />
            <Route path="/matching-category/:tier/:categorySlug" element={user ? <MatchingCategoryPage /> : <Navigate to="/" />} />
            <Route path="/matching/:id" element={user ? <MatchingGameViewer /> : <Navigate to="/" />} />
            <Route path="/study-guide-center" element={user ? <StudyGuideCenter /> : <Navigate to="/" />} />
            <Route path="/study-guide-category/:tier/:categorySlug" element={user ? <StudyGuideCategoryPage /> : <Navigate to="/" />} />
            <Route path="/study-guides/:id" element={user ? <StudyGuideViewer /> : <Navigate to="/" />} />
            <Route path="/memory-game-center" element={user ? <MemoryGameCenter /> : <Navigate to="/" />} />
            <Route path="/memory-game-category/:tier/:categorySlug" element={user ? <MemoryGameCategoryPage /> : <Navigate to="/" />} />
            <Route path="/memory-game/:id" element={user ? <MemoryGameViewer /> : <Navigate to="/" />} />
            <Route path="/fill-in-center" element={user ? <FillInCenter /> : <Navigate to="/" />} />
            <Route path="/fill-in-category/:tier/:categorySlug" element={user ? <FillInCategoryPage /> : <Navigate to="/" />} />
            <Route path="/fill-in/:id" element={user ? <FillInViewer /> : <Navigate to="/" />} />
            <Route path="/jeopardy-center" element={user ? <JeopardyCenter /> : <Navigate to="/" />} />
            <Route path="/jeopardy-category/:tier/:categorySlug" element={user ? <JeopardyCategoryPage /> : <Navigate to="/" />} />
            <Route path="/jeopardy-game/:id" element={user ? <JeopardyGameViewer /> : <Navigate to="/" />} />
            <Route path="/publication-center" element={user ? <PublicationCenter /> : <Navigate to="/" />} />
            <Route path="/my-publications" element={user ? <MyPublications /> : <Navigate to="/" />} />
            <Route path="/publications/:id/read" element={user ? <PublicationReader /> : <Navigate to="/" />} />
            <Route path="/class-center" element={user ? <ClassCenter /> : <Navigate to="/" />} />
            <Route path="/classes/:classId" element={user ? <ClassDetail /> : <Navigate to="/" />} />
            <Route path="/lesson-player/:id" element={user ? <CoursePlayerPage /> : <Navigate to="/" />} />
            <Route path="/my-learning" element={user ? <StudentDashboard /> : <Navigate to="/" />} />
            <Route path="/student-resources" element={user ? <StudentResources /> : <Navigate to="/" />} />
            <Route path="/ernie-ai" element={user ? <ErnieAiPage /> : <Navigate to="/" />} />
            <Route path="/ernie-ai/memories" element={user ? <ErnieMemoriesPage /> : <Navigate to="/" />} />
            <Route path="/book" element={<Navigate to="/books" replace />} />
            <Route path="/books" element={<BooksLandingPage />} />
            <Route path="/books/:slug" element={<BookDetailPage />} />
            <Route path="/webmaster" element={user?.role === 'admin' ? <WebmasterPage /> : <Navigate to="/" />} />
            <Route path="/admin/books" element={user?.role === 'admin' ? <BooksManager /> : <Navigate to="/" />} />
            <Route path="/admin/media-library" element={user?.role === 'admin' ? <MediaLibraryPage /> : <Navigate to="/" />} />
            <Route path="/admin" element={user?.role === 'admin' ? <AdminDashboard /> : <Navigate to="/" />} />
            <Route path="/admin/areas" element={user?.role === 'admin' ? <AreasManagerPage /> : <Navigate to="/" />} />
            <Route path="/admin/areas/new" element={user?.role === 'admin' ? <AreaCreatePage /> : <Navigate to="/" />} />
            <Route path="/admin/areas/edit/:id" element={user?.role === 'admin' ? <AreaEditPage /> : <Navigate to="/" />} />
            <Route path="/admin/simple-course" element={user?.role === 'admin' ? <SimpleCourseGenerator /> : <Navigate to="/" />} />
            <Route path="/admin/simple-blog" element={user?.role === 'admin' ? <SimpleBlogGenerator /> : <Navigate to="/" />} />
            <Route path="/admin/simple-lesson" element={user?.role === 'admin' ? <SimpleLessonGenerator /> : <Navigate to="/" />} />
            <Route path="/admin/generation" element={user?.role === 'admin' ? <GenerationPage /> : <Navigate to="/" />} />
            <Route path="/admin/chapter-assembly" element={user?.role === 'admin' ? <ChapterAssemblyPage /> : <Navigate to="/" />} />
            <Route path="/admin/publication-assembly" element={user?.role === 'admin' ? <PublicationAssemblyPage /> : <Navigate to="/" />} />
            <Route path="/admin/publication-vault" element={user?.role === 'admin' ? <PublicationVaultPage /> : <Navigate to="/" />} />
            <Route path="/admin/publications/:id/preview" element={user?.role === 'admin' ? <PublicationReader adminPreview /> : <Navigate to="/" />} />
            <Route path="/admin/test-management" element={user?.role === 'admin' ? <TestManagementPage /> : <Navigate to="/" />} />
            <Route path="/admin/quiz-management" element={user?.role === 'admin' ? <QuizManagementPage /> : <Navigate to="/" />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/donation-success" element={<DonationSuccess />} />
            <Route path="/booking-success" element={<BookingSuccess />} />
          </Routes>
        </div>
        </SiteContentProvider>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;